import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client.ts'
import { Avatar } from '../components/Avatar.tsx'
import { lastSeenText, plural } from '../lib/format.ts'
import { useAuth } from '../store/auth.ts'

type Overview = {
  people: { total: number; activeDay: number; activeWeek: number }
  families: number
  chats: { total: number; groups: number; direct: number }
  messages: { total: number; day: number; week: number }
  files: { count: number; bytes: number }
  sessions: number
  pushSubscriptions: number
  activeInvites: number
  database: { bytes: number }
  sections: Record<string, number>
  server: { uptimeSeconds: number; nodeVersion: string; memoryMb: number; startedAt: string }
}

type AdminUser = {
  id: string
  email: string | null
  displayName: string
  avatarUrl: string | null
  isAdmin: boolean
  createdAt: string
  lastSeenAt: string | null
  online: boolean
  families: { id: string; name: string; role: string }[]
  messagesCount: number
  devices: number
  sessions: number
}

type ActivityEvent = {
  id: string
  action: string
  entityType: string
  createdAt: string
  actor: { id: string; displayName: string } | null
}

const SECTION_NAMES: Record<string, string> = {
  shoppingLists: 'Списки покупок',
  shoppingItems: 'Пунктов в списках',
  events: 'События',
  tasks: 'Дела',
  movies: 'Фильмы',
  wishItems: 'Желания',
  healthRecords: 'Записи о здоровье',
}

const ROLE_NAMES: Record<string, string> = {
  OWNER: 'владелец',
  ADULT: 'взрослый',
  TEEN: 'подросток',
  CHILD: 'ребёнок',
}

function bytes(value: number): string {
  if (value < 1024) return `${value} Б`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} КБ`
  if (value < 1024 * 1024 * 1024) return `${(value / 1024 / 1024).toFixed(1)} МБ`
  return `${(value / 1024 / 1024 / 1024).toFixed(2)} ГБ`
}

function uptime(seconds: number): string {
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (d > 0) return `${d} ${plural(d, 'день', 'дня', 'дней')} ${h} ч`
  if (h > 0) return `${h} ч ${m} мин`
  return `${m} мин`
}

const dateTime = (iso: string) =>
  new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))

export function AdminPage() {
  const [overview, setOverview] = useState<Overview | null>(null)
  const [users, setUsers] = useState<AdminUser[]>([])
  const me = useAuth((s) => s.user)
  const [removing, setRemoving] = useState<string | null>(null)

  /**
   * Удалить учётную запись.
   *
   * Сообщения при этом остаются — только без имени автора. Переписка
   * семьи это её общая память, и уход одного не должен выдирать из неё
   * куски. Говорим об этом заранее, а не постфактум.
   */
  const removeUser = async (user: AdminUser) => {
    const lonely = user.families.length === 1
    const warning =
      `Удалить «${user.displayName}» (${user.email ?? 'без почты'})?\n\n` +
      `Сообщения останутся, но без имени автора.` +
      (lonely ? '\nСемья, где он один, тоже будет удалена.' : '')

    if (!window.confirm(warning)) return

    setRemoving(user.id)
    try {
      await api(`/api/admin/users/${user.id}`, { method: 'DELETE' })
      setUsers((list) => list.filter((u) => u.id !== user.id))
    } catch (failure) {
      window.alert(failure instanceof Error ? failure.message : 'Не вышло удалить')
    } finally {
      setRemoving(null)
    }
  }
  const [activity, setActivity] = useState<ActivityEvent[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true

    const load = async () => {
      try {
        const [o, u, a] = await Promise.all([
          api<Overview>('/api/admin/overview'),
          api<{ users: AdminUser[] }>('/api/admin/users'),
          api<{ events: ActivityEvent[] }>('/api/admin/activity?limit=40'),
        ])
        if (!alive) return
        setOverview(o)
        setUsers(u.users)
        setActivity(a.events)
        setError(null)
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : 'Не удалось загрузить')
      }
    }

    void load()
    // Обновляем нечасто: страница для наблюдения, а не для слежения в реальном времени
    const timer = window.setInterval(() => void load(), 30_000)
    return () => {
      alive = false
      window.clearInterval(timer)
    }
  }, [])

  if (error) {
    return (
      <div className="admin">
        <div className="admin-head">
          <Link to="/" className="admin-back">← К перепискам</Link>
        </div>
        <p className="empty-hint">{error}</p>
      </div>
    )
  }

  if (!overview) {
    return (
      <div className="center-screen">
        <div className="spinner" />
      </div>
    )
  }

  return (
    <div className="admin scroll">
      <header className="admin-head">
        <Link to="/" className="admin-back">← К перепискам</Link>
        <h1>Сервер</h1>
      </header>

      <p className="admin-note">
        Здесь видно, как работает приложение. Содержимое переписок, дневников здоровья
        и личных календарей сюда не попадает — только счётчики.
      </p>

      <section className="tiles">
        <Tile label="Человек всего" value={overview.people.total}
              note={`${overview.people.activeDay} за сутки · ${overview.people.activeWeek} за неделю`} />
        <Tile label="Семей" value={overview.families} note={`${overview.chats.total} чатов`} />
        <Tile label="Сообщений" value={overview.messages.total}
              note={`${overview.messages.day} за сутки · ${overview.messages.week} за неделю`} />
        <Tile label="Файлов" value={overview.files.count} note={bytes(overview.files.bytes)} />
        <Tile label="База данных" value={bytes(overview.database.bytes)} note="на диске" />
        <Tile label="Служба работает" value={uptime(overview.server.uptimeSeconds)}
              note={`${overview.server.memoryMb} МБ памяти · Node ${overview.server.nodeVersion}`} />
        <Tile label="Активных входов" value={overview.sessions}
              note={`${overview.pushSubscriptions} устройств с уведомлениями`} />
        <Tile label="Приглашений" value={overview.activeInvites} note="не использовано" />
      </section>

      <h2 className="admin-h2">Чем пользуются</h2>
      <section className="sections-grid">
        {Object.entries(overview.sections).map(([key, count]) => (
          <div key={key} className={'section-stat' + (count === 0 ? ' is-empty' : '')}>
            <span className="section-stat-name">{SECTION_NAMES[key] ?? key}</span>
            <span className="section-stat-value">{count}</span>
          </div>
        ))}
      </section>

      <h2 className="admin-h2">Люди</h2>
      <section className="admin-table">
        {users.map((user) => (
          <div key={user.id} className="admin-row">
            <Avatar id={user.id} name={user.displayName} url={user.avatarUrl} size={38} online={user.online} />

            <div className="admin-row-main">
              <div className="admin-row-name">
                {user.displayName}
                {user.isAdmin && <span className="tag tag-admin">администратор</span>}
              </div>
              <div className="admin-row-sub">{user.email ?? 'без почты'}</div>
            </div>

            <div className="admin-row-families">
              {user.families.map((f) => (
                <span key={f.id} className="tag">
                  {f.name} · {ROLE_NAMES[f.role] ?? f.role}
                </span>
              ))}
            </div>

            <div className="admin-row-stats">
              <span title="Сообщений отправлено">{user.messagesCount} сообщ.</span>
              <span title="Устройств с уведомлениями">{user.devices} устр.</span>
            </div>

            <div className="admin-row-seen">
              {lastSeenText(user.lastSeenAt, user.online)}
              <span className="admin-row-since">с {dateTime(user.createdAt)}</span>
            </div>

            {/**
             * Удаление учётной записи.
             *
             * Нужно там, где ошиблись при заведении. Спрашиваем прямо
             * и с именем: строки в списке похожи одна на другую,
             * и промахнуться на соседнюю проще, чем кажется.
             */}
            {user.id !== me?.id && (
              <button
                className="admin-row-remove"
                title="Удалить учётную запись"
                onClick={() => void removeUser(user)}
                disabled={removing === user.id}
              >
                {removing === user.id ? '…' : '✕'}
              </button>
            )}
          </div>
        ))}
      </section>

      <h2 className="admin-h2">Последние изменения</h2>
      <section className="admin-activity">
        {activity.length === 0 && <p className="empty-hint">Пока ничего не происходило</p>}
        {activity.map((event) => (
          <div key={event.id} className="activity-row">
            <span className="activity-time">{dateTime(event.createdAt)}</span>
            <span className="activity-who">{event.actor?.displayName ?? 'система'}</span>
            <span className="activity-what">
              {event.action} · {event.entityType}
            </span>
          </div>
        ))}
      </section>
    </div>
  )
}

function Tile({ label, value, note }: { label: string; value: string | number; note?: string }) {
  return (
    <div className="tile">
      <div className="tile-value">{value}</div>
      <div className="tile-label">{label}</div>
      {note && <div className="tile-note">{note}</div>}
    </div>
  )
}
